<template>
    <div class="gpu-page">
        <main class="page-shell">
            <section class="card hero-card">
                <div class="section-title">
                    <span class="num">01</span>
                    <span class="bar">|</span>
                    <span>{{ getLabel('index.sectionIntro', '平台介绍') }}</span>
                </div>
                <div class="hero-bg"></div>
                <div class="gpu-box">
                    <div class="fan"></div>
                    <div class="fan"></div>
                    <div class="fan"></div>
                </div>
                <div class="platform-ring"></div>
                <div class="hero-content">
                    <h1>{{ getLabel('index.heroTitle', 'AI GPU') }}</h1>
                    <h2>{{ getLabel('index.heroSubtitle', '算力租赁平台') }}</h2>
                    <ul class="bullets">
                        <li>{{ getLabel('index.heroBullet1', '参与 GPU 节点部署') }}</li>
                        <li>{{ getLabel('index.heroBullet2', '共享全球 AI 算力租赁收益') }}</li>
                    </ul>
                    <div class="desc">
                        {{ getLabel('index.heroDesc1', '全球 GPU Compute Network') }}<br>
                        {{ getLabel('index.heroDesc2', '面向 AI 训练、推理与高性能计算场景') }}<br>
                        {{ getLabel('index.heroDesc3', '提供稳定高效的算力资源服务') }}
                    </div>
                    <button type="button" class="hero-btn" @click="openVast">
                        {{ getLabel('index.visitVast', '访问 Vast.ai 官网') }}
                        <span>↗</span>
                    </button>
                </div>
            </section>

            <section class="card">
                <div class="section-title">
                    <span class="num">02</span>
                    <span class="bar">|</span>
                    <span>{{ getLabel('index.sectionFlow', '平台运行逻辑') }}</span>
                </div>
                <div class="flow-wrap">
                    <div class="icons-column">
                        <div
                            v-for="(step, index) in flowSteps"
                            :key="step.title"
                            class="icon-stack"
                        >
                            <div class="icon-circle">{{ step.icon }}</div>
                            <div v-if="index !== flowSteps.length - 1" class="dotline"></div>
                        </div>
                    </div>
                    <div class="steps-column">
                        <div
                            v-for="step in flowSteps"
                            :key="step.title"
                            class="step-card"
                        >
                            <div class="step-title">{{ step.title }}</div>
                            <p>{{ step.desc1 }}<br>{{ step.desc2 }}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card">
                <div class="section-title">
                    <span class="num">03</span>
                    <span class="bar">|</span>
                    <span>{{ getLabel('index.sectionFeature', 'Vast.ai 生态能力') }}</span>
                </div>
                <div class="feature-grid">
                    <div
                        v-for="feature in features"
                        :key="feature.title"
                        class="feature-card"
                    >
                        <div class="feature-icon">{{ feature.icon }}</div>
                        <h3>{{ feature.title }}</h3>
                        <p>{{ feature.desc }}</p>
                    </div>
                </div>
            </section>

            <section class="card">
                <div class="section-title">
                    <span class="num">04</span>
                    <span class="bar">|</span>
                    <span>{{ getLabel('index.sectionHotGpu', '热门 GPU 节点') }}</span>
                </div>
                <div class="gpu-grid">
                    <div
                        v-for="gpu in hotGpuCards"
                        :key="gpu.title"
                        class="gpu-card"
                    >
                        <div class="gpu-img" :style="{ backgroundImage: 'url(' + gpu.image + ')' }"></div>
                        <h3>{{ gpu.title }}</h3>
                        <p>{{ gpu.subtitle }}</p>
                        <ul>
                            <li v-for="bullet in gpu.bullets" :key="bullet">{{ bullet }}</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section class="card">
                <div class="section-title">
                    <span class="num">05</span>
                    <span class="bar">|</span>
                    <span>{{ getLabel('index.sectionReason', '为什么选择 GPU 算力') }}</span>
                </div>
                <div class="reason-list">
                    <div
                        v-for="reason in reasons"
                        :key="reason.title"
                        class="reason-card"
                    >
                        <div class="reason-icon">{{ reason.icon }}</div>
                        <div>
                            <h3>{{ reason.title }}</h3>
                            <p>{{ reason.desc1 }}<br>{{ reason.desc2 }}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card footer-card">
                <div class="section-title">
                    <span class="num">06</span>
                    <span class="bar">|</span>
                    <span>{{ getLabel('index.sectionAccess', 'Vast.ai 全球算力节点接入') }}</span>
                </div>
                <p class="desc footer-desc">
                    {{ getLabel('index.footerDesc1', '平台 GPU 节点已接入 Vast.ai 全球 AI 算力市场') }}<br>
                    {{ getLabel('index.footerDesc2', '面向 AI 开发者、研究机构与企业用户') }}<br>
                    {{ getLabel('index.footerDesc3', '提供 GPU 算力租赁服务') }}
                </p>
                <div class="support-list">
                    <span v-for="support in supportItems" :key="support.label">
                        <b>{{ support.icon }}</b>
                        <em>{{ support.label }}</em>
                    </span>
                </div>
                <div class="earth"></div>
            </section>
        </main>

        <nav class="bottom-nav">
            <router-link to="/index" class="nav-item active">
                <van-icon name="home-o" />
                <span>{{ $t('home.index') }}</span>
            </router-link>
            <router-link to="/item" class="nav-item">
                <van-icon name="chart-trending-o" />
                <span>{{ getLabel('index.navSubscription', '算力认购') }}</span>
            </router-link>
            <router-link to="/user" class="nav-item">
                <van-icon name="contact" />
                <span>{{ $t('home.my') }}</span>
            </router-link>
        </nav>

        <button
            type="button"
            class="service-float"
            :aria-label="getLabel('user.onlineService', '在线客服')"
            @click="openKefu"
        >
            <van-icon name="service-o" />
        </button>

        <van-dialog
            v-model="showPopup"
            theme="round-button"
            :confirmButtonText="$t('utils.confirm')"
            @confirm="showPopup = false"
        >
            <div class="popup-close" @click="showPopup = false">
                <van-icon name="cross" />
            </div>
            <div class="popup-body" v-html="popupContent"></div>
        </van-dialog>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Dialog, Icon } from "vant";

Vue.use(Dialog);
Vue.use(Icon);

const gpuCardImages = {
    b200: require("../images/home/b200.jpg"),
    h200: require("../images/home/h200.jpg"),
    h100: require("../images/home/h100.jpg"),
    rtx4090: require("../images/home/4090.jpg"),
};

const gpuPresets = [
    {
        title: "NVIDIA B200",
        subtitle: "Blackwell 架构旗舰 AI GPU",
        bullets: ["192GB HBM3e 显存", "超大规模 AI 模型训练", "下一代 AI Cluster 核心节点"],
        image: gpuCardImages.b200,
    },
    {
        title: "NVIDIA H200",
        subtitle: "Hopper 架构升级版 AI GPU",
        bullets: ["141GB HBM3e 显存", "高性能 AI 推理与训练", "企业级 AI 算力部署"],
        image: gpuCardImages.h200,
    },
    {
        title: "NVIDIA H100",
        subtitle: "企业级 AI Cluster 节点",
        bullets: ["80GB HBM3 显存", "高性能训练与推理", "适用于大型 AI 模型"],
        image: gpuCardImages.h100,
    },
    {
        title: "RTX 4090",
        subtitle: "高性能 AI 推理节点",
        bullets: ["24GB GDDR6X 显存", "高性价比 GPU 节点", "适合入门级 AI 算力部署"],
        image: gpuCardImages.rtx4090,
    },
];

export default {
    name: "Index",
    data() {
        return {
            lang: this.$i18n.locale || "en_us",
            data: {},
            projectItems: [],
            showPopup: false,
            popupContent: "",
            appOriginalStyles: null,
            bodyOriginalBackground: "",
        };
    },
    computed: {
        flowSteps() {
            return [
                { icon: "👤", title: this.getLabel("index.flowTitle1", "用户认购 GPU 份额"), desc1: this.getLabel("index.flowDesc1_1", "用户参与 GPU 算力份额认购"), desc2: this.getLabel("index.flowDesc1_2", "平台统一管理 GPU 硬件资源") },
                { icon: "▦", title: this.getLabel("index.flowTitle2", "公司采购 GPU 设备"), desc1: this.getLabel("index.flowDesc2_1", "公司采购高性能 GPU 设备"), desc2: this.getLabel("index.flowDesc2_2", "构建 AI GPU 算力节点") },
                { icon: "◎", title: this.getLabel("index.flowTitle3", "接入 Vast.ai 市场"), desc1: this.getLabel("index.flowDesc3_1", "GPU 节点接入 Vast.ai 全球算力市场"), desc2: this.getLabel("index.flowDesc3_2", "面向 AI 开发者与企业用户开放租赁") },
                { icon: "$", title: this.getLabel("index.flowTitle4", "GPU 租赁产生收益"), desc1: this.getLabel("index.flowDesc4_1", "GPU 节点出租后产生租赁收益"), desc2: this.getLabel("index.flowDesc4_2", "平台根据认购份额进行收益分配") },
            ];
        },
        features() {
            return [
                { icon: "▣", title: this.getLabel("index.featureTitle1", "AI Compute"), desc: this.getLabel("index.featureDesc1", "面向 AI 训练与推理场景，提供高性能 GPU 算力支持") },
                { icon: "▦", title: this.getLabel("index.featureTitle2", "GPU Cluster"), desc: this.getLabel("index.featureDesc2", "支持多节点 GPU Cluster 部署，满足企业级 AI 计算需求") },
                { icon: "☁", title: this.getLabel("index.featureTitle3", "Node Hosting"), desc: this.getLabel("index.featureDesc3", "平台统一运维与节点管理，保障 GPU 节点稳定运行") },
                { icon: "◇", title: this.getLabel("index.featureTitle4", "Cloud Infrastructure"), desc: this.getLabel("index.featureDesc4", "构建稳定、高效、可扩展的全球 GPU Compute Network") },
            ];
        },
        hotGpuCards() {
            const subtitles = [
                this.getLabel("index.gpuSubtitle1", "Blackwell 架构旗舰 AI GPU"),
                this.getLabel("index.gpuSubtitle2", "Hopper 架构升级版 AI GPU"),
                this.getLabel("index.gpuSubtitle3", "企业级 AI Cluster 节点"),
                this.getLabel("index.gpuSubtitle4", "高性能 AI 推理节点"),
            ];
            const bulletGroups = [
                [
                    this.getLabel("index.gpuBullet1_1", "192GB HBM3e 显存"),
                    this.getLabel("index.gpuBullet1_2", "超大规模 AI 模型训练"),
                    this.getLabel("index.gpuBullet1_3", "下一代 AI Cluster 核心节点"),
                ],
                [
                    this.getLabel("index.gpuBullet2_1", "141GB HBM3e 显存"),
                    this.getLabel("index.gpuBullet2_2", "高性能 AI 推理与训练"),
                    this.getLabel("index.gpuBullet2_3", "企业级 AI 算力部署"),
                ],
                [
                    this.getLabel("index.gpuBullet3_1", "80GB HBM3 显存"),
                    this.getLabel("index.gpuBullet3_2", "高性能训练与推理"),
                    this.getLabel("index.gpuBullet3_3", "适用于大型 AI 模型"),
                ],
                [
                    this.getLabel("index.gpuBullet4_1", "24GB GDDR6X 显存"),
                    this.getLabel("index.gpuBullet4_2", "高性价比 GPU 节点"),
                    this.getLabel("index.gpuBullet4_3", "适合入门级 AI 算力部署"),
                ],
            ];
            return gpuPresets.map((preset, index) => {
                return {
                    ...preset,
                    subtitle: subtitles[index],
                    bullets: bulletGroups[index],
                    image: preset.image,
                };
            });
        },
        reasons() {
            return [
                { icon: "↗", title: this.getLabel("index.reasonTitle1", "AI 行业高速增长"), desc1: this.getLabel("index.reasonDesc1_1", "全球 AI 算力需求持续增长"), desc2: this.getLabel("index.reasonDesc1_2", "GPU 已成为 AI 时代核心基础设施") },
                { icon: "◎", title: this.getLabel("index.reasonTitle2", "全球 GPU 市场"), desc1: this.getLabel("index.reasonDesc2_1", "平台节点接入 Vast.ai 全球市场"), desc2: this.getLabel("index.reasonDesc2_2", "面向全球 AI 用户提供算力服务") },
                { icon: "▣", title: this.getLabel("index.reasonTitle3", "实体 GPU 资产"), desc1: this.getLabel("index.reasonDesc3_1", "节点对应真实 GPU 硬件部署"), desc2: this.getLabel("index.reasonDesc3_2", "硬件长期稳定算力支持") },
                { icon: "盾", title: this.getLabel("index.reasonTitle4", "稳定运行保障"), desc1: this.getLabel("index.reasonDesc4_1", "平台统一运维与调度"), desc2: this.getLabel("index.reasonDesc4_2", "保障 GPU 节点稳定在线运行") },
            ];
        },
        supportItems() {
            return [
                { icon: "♙", label: this.getLabel("index.support1", "选择实例") },
                { icon: "⬡", label: this.getLabel("index.support2", "AI 模型部署") },
                { icon: "▰", label: this.getLabel("index.support3", "GPU Cluster 部署") },
                { icon: "◇", label: this.getLabel("index.support4", "高性能计算服务") },
            ];
        },
    },
    created() {
        this.$parent.footer("index", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.start();
        if (this.$router.history.current.query.agent) {
            localStorage.setItem("agent", this.$router.history.current.query.agent);
        }
    },
    beforeDestroy() {
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#030914";
            app.style.paddingBottom = "0";
            document.body.style.background = "#030914";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        async start() {
            const res = await Fetch("/index/int");
            this.data = res.data || {};
            this.projectItems = (res.data.item1 && res.data.item1.list) ? res.data.item1.list.slice(0, 4) : [];

            if (res.data.popup && Number(res.data.popup.show) === 1) {
                this.popupContent = res.data.popup.content || "";
                this.showPopup = true;
            }

            if (this.projectItems.length) {
                this.projectItems.forEach((item, index) => {
                    Fetch("/index/item_detail", { id: item.id }).then((detailRes) => {
                        if (detailRes.data && detailRes.data.img) {
                            this.$set(this.projectItems[index], "img", detailRes.data.img);
                        }
                        if (detailRes.data && !this.projectItems[index].cover && detailRes.data.cover) {
                            this.$set(this.projectItems[index], "cover", detailRes.data.cover);
                        }
                        if (detailRes.data && !this.projectItems[index].pic && detailRes.data.pic) {
                            this.$set(this.projectItems[index], "pic", detailRes.data.pic);
                        }
                    }).catch(() => {});
                });
            }
        },
        openVast() {
            window.open("https://vast.ai/", "_blank");
        },
        openKefu() {
            this.$router.push("/kefu");
        },
    },
};
</script>

<style lang="less" scoped>
.gpu-page {
    min-height: 100vh;
    background: #030914;
    color: #fff;
}

.page-shell {
    width: 100%;
    max-width: 430px;
    min-height: 100vh;
    margin: 0 auto;
    padding: 14px 14px 96px;
    background:
        radial-gradient(circle at 80% 5%, rgba(0, 92, 255, 0.35), transparent 28%),
        radial-gradient(circle at 10% 40%, rgba(103, 52, 255, 0.2), transparent 24%),
        linear-gradient(180deg, #061225 0%, #030914 100%);
    box-sizing: border-box;
}

.card {
    position: relative;
    overflow: hidden;
    margin-bottom: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 130, 255, 0.28);
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(5, 18, 38, 0.94), rgba(4, 13, 28, 0.86));
    box-shadow: inset 0 0 28px rgba(0, 95, 255, 0.12);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    font-size: 17px;
    font-weight: 700;
}

.num {
    color: #7b4dff;
    font-size: 20px;
    font-weight: 800;
}

.bar {
    color: #1d7dff;
}

.hero-card {
    min-height: 420px;
    padding-top: 20px;
}

.hero-bg {
    position: absolute;
    right: -35px;
    top: 20px;
    width: 260px;
    height: 220px;
    border-radius: 50%;
    background:
        radial-gradient(circle at center, rgba(32, 109, 255, 0.38), transparent 55%),
        linear-gradient(90deg, transparent, rgba(0, 120, 255, 0.12));
}

.gpu-box {
    position: absolute;
    right: 10px;
    top: 55px;
    z-index: 2;
    width: 210px;
    height: 126px;
    padding: 10px;
    border: 2px solid rgba(95, 154, 255, 0.75);
    border-radius: 18px;
    background: linear-gradient(135deg, #101827, #020713);
    box-shadow: 0 0 32px rgba(0, 98, 255, 0.58);
    display: flex;
    align-items: center;
    justify-content: space-around;
}

.fan {
    width: 54px;
    height: 54px;
    border: 2px solid #355b91;
    border-radius: 50%;
    background: radial-gradient(circle, #0c1220 15%, #030712 18%, #17243a 45%, #050914 48%);
    box-shadow: inset 0 0 16px rgba(25, 112, 255, 0.65);
}

.platform-ring {
    position: absolute;
    right: -5px;
    top: 190px;
    width: 255px;
    height: 58px;
    border: 3px solid #1684ff;
    border-radius: 50%;
    box-shadow: 0 0 28px #136fff, 0 0 60px rgba(0, 119, 255, 0.45);
    opacity: 0.9;
}

.hero-content {
    position: relative;
    z-index: 3;
    max-width: 205px;
}

h1 {
    margin: 30px 0 8px;
    font-size: 48px;
    line-height: 1;
    letter-spacing: -1px;
    font-weight: 900;
    background: linear-gradient(90deg, #8d56ff, #2b8cff);
    -webkit-background-clip: text;
    color: transparent;
}

h2 {
    margin: 0 0 16px;
    font-size: 31px;
    line-height: 1.15;
    font-weight: 900;
}

.bullets {
    margin: 0 0 18px;
    padding-left: 18px;
    font-size: 14px;
    line-height: 1.9;
}

.desc {
    margin-bottom: 18px;
    color: #d9e9ff;
    font-size: 13px;
    line-height: 1.75;
}

.hero-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border: 0;
    border-radius: 7px;
    background: linear-gradient(90deg, #4a2cff, #1c6bff);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 0 18px rgba(46, 111, 255, 0.35);
    cursor: pointer;
}

.flow-wrap {
    display: grid;
    grid-template-columns: 95px 1fr;
    gap: 12px;
    align-items: start;
}

.icons-column {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.icon-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.icon-circle {
    width: 58px;
    height: 58px;
    border: 1px solid rgba(111, 78, 255, 0.75);
    border-radius: 50%;
    background: radial-gradient(circle, #123dff, #06142d 70%);
    display: grid;
    place-items: center;
    font-size: 28px;
}

.dotline {
    width: 2px;
    height: 18px;
    background: linear-gradient(#0af, #7552ff);
}

.steps-column {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.step-card,
.feature-card,
.gpu-card,
.reason-card {
    border: 1px solid rgba(0, 126, 255, 0.22);
    border-radius: 8px;
    background: rgba(5, 23, 50, 0.72);
}

.step-card {
    padding: 12px;
}

.step-title {
    margin-bottom: 4px;
    font-size: 16px;
    font-weight: 800;
}

.step-card p,
.feature-card p,
.gpu-card p,
.reason-card p {
    margin: 0;
    color: #d4e4f7;
    font-size: 12px;
    line-height: 1.55;
}

.feature-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.feature-card {
    min-height: 125px;
    padding: 16px 12px;
    text-align: center;
}

.feature-icon {
    margin: 4px 0 8px;
    color: #098cff;
    font-size: 38px;
}

.feature-card h3,
.reason-card h3 {
    margin: 0 0 6px;
    color: #00d5ff;
    font-size: 15px;
}

.gpu-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.gpu-card {
    padding: 12px;
    cursor: pointer;
}

.gpu-img {
    height: 58px;
    margin-bottom: 8px;
    border-radius: 8px;
    background: linear-gradient(135deg, #111, #263342 50%, #05070a);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    overflow: hidden;
}

.gpu-card h3 {
    margin: 0 0 4px;
    font-size: 16px;
}

.gpu-card ul {
    margin: 8px 0 0;
    padding-left: 15px;
    color: #d4e4f7;
    font-size: 11px;
    line-height: 1.55;
}

.gpu-card li::marker {
    color: #704cff;
}

.reason-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.reason-card {
    display: grid;
    grid-template-columns: 46px 1fr;
    gap: 10px;
    align-items: center;
    padding: 12px;
}

.reason-icon {
    color: #177cff;
    text-align: center;
    font-size: 31px;
}

.footer-card {
    min-height: 190px;
}

.footer-desc {
    position: relative;
    z-index: 2;
}

.support-list {
    display: flex;
    gap: 18px;
    margin-top: 18px;
    color: #b9cff0;
    font-size: 11px;
    position: relative;
    z-index: 2;
}

.support-list span {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
}

.support-list b {
    font-size: 14px;
}

.support-list em {
    font-style: normal;
    font-weight: 700;
}

.earth {
    position: absolute;
    right: -65px;
    bottom: -90px;
    width: 245px;
    height: 155px;
    border-top: 3px solid #157eff;
    border-radius: 50%;
    box-shadow: 0 -18px 40px rgba(0, 116, 255, 0.7);
    background: radial-gradient(circle at 40% 20%, rgba(0, 132, 255, 0.75), transparent 45%);
}

.bottom-nav {
    position: fixed;
    left: 50%;
    bottom: 0;
    z-index: 50;
    width: 100%;
    max-width: 430px;
    padding: 9px 8px max(9px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.92);
    backdrop-filter: blur(16px);
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: space-around;
    box-sizing: border-box;
}

.nav-item {
    min-width: 74px;
    padding: 7px 13px;
    color: #6b7280;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.nav-item .van-icon {
    font-size: 23px;
}

.nav-item span {
    margin-top: 5px;
    font-size: 11px;
    line-height: 1.25;
    font-weight: 500;
}

.nav-item.active,
.nav-item.router-link-exact-active {
    color: #2d5bff;
}

.nav-item.active span,
.nav-item.router-link-exact-active span {
    font-weight: 700;
}

.service-float {
    position: fixed;
    right: max(18px, calc((100vw - 430px) / 2 + 18px));
    bottom: max(90px, calc(env(safe-area-inset-bottom) + 90px));
    z-index: 80;
    width: 54px;
    height: 54px;
    border: 1px solid rgba(156, 190, 255, 0.58);
    border-radius: 50%;
    background: linear-gradient(145deg, #337cff, #5939f3);
    color: #fff;
    box-shadow: 0 0 0 6px rgba(45, 91, 255, 0.1), 0 9px 24px rgba(21, 75, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.service-float .van-icon {
    font-size: 28px;
}

.popup-close {
    position: absolute;
    right: 15px;
    top: 15px;
    z-index: 3;
    color: #999;
}

.popup-close .van-icon {
    font-size: 20px;
}

.popup-body {
    max-height: 60vh;
    padding: 40px 20px 20px;
    overflow-y: auto;
    color: #1f2937;
    font-size: 14px;
    line-height: 1.7;
}

@media (max-width: 390px) {
    .page-shell {
        padding: 10px 10px 96px;
    }

    h1 {
        font-size: 42px;
    }

    h2 {
        font-size: 28px;
    }

    .gpu-box {
        width: 190px;
        right: -2px;
    }

    .hero-content {
        max-width: 190px;
    }
}
</style>

<style>
.gpu-page .van-dialog {
    border-radius: 12px;
    color: #1f2937;
}

.gpu-page .van-dialog__confirm,
.gpu-page .van-button--default,
.gpu-page .van-button--large {
    background: #4f46e5 !important;
    background-image: none !important;
    border: 1px solid #4f46e5 !important;
    color: #fff !important;
}

.gpu-page .van-dialog__confirm {
    height: 44px;
    line-height: 44px;
    border-radius: 22px;
    margin: 0 15px 15px;
}

.gpu-page .popup-body,
.gpu-page .popup-body p,
.gpu-page .popup-body div,
.gpu-page .popup-body span,
.gpu-page .popup-body li,
.gpu-page .popup-body td,
.gpu-page .popup-body th,
.gpu-page .popup-body strong,
.gpu-page .popup-body b,
.gpu-page .popup-body a {
    color: #1f2937 !important;
}

.gpu-page .popup-body img {
    max-width: 100%;
    height: auto;
}
</style>
